import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';

import type { VideoAsset, VideoUploadAdapter, VideoUploadFileMeta, VideoUploadLimits, VideoUploadPart } from '@/types';

import { VIDEO_UPLOAD_EXTENSIONS, VIDEO_UPLOAD_MULTIPART_THRESHOLD_BYTES, VIDEO_UPLOAD_TYPES } from './constants';
import { readVideoDuration } from './readVideoDuration';

interface VideoUploadOptions {
  adapter?: VideoUploadAdapter;
  onCompleted: (video: VideoAsset) => void;
  limits: VideoUploadLimits;
}

export type VideoUploadPhase = 'idle' | 'checking' | 'uploading' | 'confirming' | 'completed' | 'error';
export type VideoUploadErrorKind = 'validation' | 'transfer' | 'completion';

export interface VideoUploadState {
  phase: VideoUploadPhase;
  progress: number;
  fileName: string | null;
  errorKey: string | null;
  errorKind: VideoUploadErrorKind | null;
  // The picked file's duration, set with a validation error so a video over the
  // duration limit can say how long it is.
  durationInMs?: number | null;
}

const IDLE: VideoUploadState = { phase: 'idle', progress: 0, fileName: null, errorKey: null, errorKind: null };

// The content type the backend expects for a filename, derived from its extension
// rather than the browser-reported MIME (which is often empty or wrong).
export const videoUploadTypeFor = (fileName: string): string | undefined => {
  const dot = fileName.lastIndexOf('.');
  const extension = dot === -1 ? '' : fileName.slice(dot).toLowerCase();
  return VIDEO_UPLOAD_TYPES[extension];
};
// Returns the translation key for why a file is rejected, or null when it passes.
export const videoValidationError = (file: File, limits: VideoUploadLimits): string | null => {
  if (file.size <= 0) return 'video.error_empty';
  if (file.size > limits.max_bytes) return 'video.error_too_large';
  // The backend keys off the extension and the upload declares the extension-derived
  // content type, so validate on the extension alone. The browser MIME is unreliable
  // (Chrome and Firefox report .m4v as video/mp4, only Safari uses video/x-m4v).
  if (!videoUploadTypeFor(file.name)) return 'video.error_unsupported';
  return null;
};

// Whole seconds, as a player shows them, so a video that reads 60:00 passes a 60 min
// limit. A duration the browser couldn't read is left to the backend probe.
export const videoDurationError = (durationInMs: number | null, limits: VideoUploadLimits): string | null =>
  durationInMs !== null && Math.floor(durationInMs / 1000) * 1000 > limits.max_duration_in_ms
    ? 'video.error_too_long'
    : null;

/**
 * Drives a single video upload through Uppy: validate, authorize a presigned S3
 * PUT, stream the bytes straight to storage, then confirm. All Swarm/Orchid
 * networking is injected, so the hook stays app-agnostic.
 */
export const useVideoUpload = ({ adapter, onCompleted, limits }: VideoUploadOptions) => {
  // A primitive, so a new limits object with the same values keeps the Uppy instance
  // and any upload in flight.
  const maxBytes = limits.max_bytes;
  const [state, setState] = useState<VideoUploadState>(IDLE);

  // Refs so the once-registered Uppy handlers always see current values.
  const phaseRef = useRef<VideoUploadPhase>('idle');
  const errorKindRef = useRef<VideoUploadErrorKind | null>(null);
  const videoIdRef = useRef<string | null>(null);
  // Uppy may abort a session after the current upload has been reset or replaced.
  const multipartVideoIdsRef = useRef(new Map<string, string>());
  const lastFileRef = useRef<File | null>(null);
  // Bumped by every start/retry/cancel; async work bails when its token is stale.
  const opRef = useRef(0);
  const callbacksRef = useRef({ adapter, onCompleted });
  callbacksRef.current = { adapter, onCompleted };

  const apply = useCallback((next: VideoUploadState) => {
    phaseRef.current = next.phase;
    errorKindRef.current = next.errorKind;
    setState(next);
  }, []);

  // Created in an effect so StrictMode's simulated unmount replaces the destroyed instance.
  const [uppy, setUppy] = useState<Uppy | null>(null);
  const hasAdapter = Boolean(adapter);
  useEffect(() => {
    if (!hasAdapter) return;

    // Shared authorization step for both transports. Ties the request to the
    // operation that started it so a canceled or superseded one can't resolve and
    // clobber a newer upload's video id, and records the minted id for later calls.
    const requestUpload = async (
      file: { name?: string; type?: string; size?: number | null },
      multipart: boolean,
      signal?: AbortSignal,
    ) => {
      const opId = opRef.current;
      const meta: VideoUploadFileMeta = {
        filename: file.name ?? '',
        // Declare the extension-derived type; the backend requires it to match the
        // extension, and the browser MIME can be empty or a benign variant.
        content_type: videoUploadTypeFor(file.name ?? '') ?? file.type ?? '',
        byte_size: file.size ?? 0,
      };
      const result = await callbacksRef.current.adapter?.request(meta, { multipart, signal });
      if (opRef.current !== opId || signal?.aborted) {
        if (result?.direct_upload.transport === 's3_multipart') {
          await callbacksRef.current.adapter?.abort(result.video.id, { upload_id: result.direct_upload.upload_id });
        }
        throw new Error('upload superseded');
      }
      if (!result) throw new Error('upload authorization failed');
      videoIdRef.current = result.video.id;
      return result;
    };

    const requireVideoId = (uploadId: string): string => {
      const videoId = multipartVideoIdsRef.current.get(uploadId);
      if (!videoId) throw new Error('missing video id');
      return videoId;
    };

    const instance = new Uppy({
      autoProceed: false,
      restrictions: { maxNumberOfFiles: 1, maxFileSize: maxBytes, allowedFileTypes: VIDEO_UPLOAD_EXTENSIONS },
    }).use(AwsS3, {
      id: 'AwsS3',
      // Large files go multipart; small ones keep the single presigned PUT.
      shouldUseMultipart: (file) => (file.size ?? 0) > VIDEO_UPLOAD_MULTIPART_THRESHOLD_BYTES,
      async getUploadParameters(file, options) {
        const result = await requestUpload(file, false, options?.signal);
        if (result.direct_upload.transport !== 's3') throw new Error('unexpected upload transport');
        return {
          method: 'PUT' as const,
          url: result.direct_upload.url,
          fields: {},
          headers: result.direct_upload.headers,
        };
      },
      async createMultipartUpload(file) {
        const result = await requestUpload(file, true);
        if (result.direct_upload.transport !== 's3_multipart') throw new Error('unexpected upload transport');
        multipartVideoIdsRef.current.set(result.direct_upload.upload_id, result.video.id);
        return { uploadId: result.direct_upload.upload_id, key: result.direct_upload.key };
      },
      async signPart(_file, { uploadId, partNumber, signal }) {
        const result = await callbacksRef.current.adapter?.signPart(requireVideoId(uploadId), {
          upload_id: uploadId,
          part_number: partNumber,
          signal,
        });
        if (!result) throw new Error('sign part failed');
        return { method: 'PUT' as const, url: result.url };
      },
      async listParts(_file, { uploadId, signal }) {
        if (!uploadId) return [];
        const result = await callbacksRef.current.adapter?.listParts(requireVideoId(uploadId), {
          upload_id: uploadId,
          signal,
        });
        if (!result) return [];
        return result.parts.map((part) => ({ PartNumber: part.part_number, ETag: part.etag, Size: part.size }));
      },
      async completeMultipartUpload(_file, { uploadId, parts, signal }) {
        const apiParts: VideoUploadPart[] = parts.map((part) => ({
          part_number: part.PartNumber ?? 0,
          etag: part.ETag ?? '',
        }));
        const result = await callbacksRef.current.adapter?.complete(requireVideoId(uploadId), {
          upload_id: uploadId,
          parts: apiParts,
          signal,
        });
        if (!result) throw new Error('complete failed');
        multipartVideoIdsRef.current.delete(uploadId);
        return { location: result.location ?? undefined };
      },
      async abortMultipartUpload(_file, { uploadId, signal }) {
        if (!uploadId) return;
        const videoId = multipartVideoIdsRef.current.get(uploadId);
        if (!videoId) return;
        multipartVideoIdsRef.current.delete(uploadId);
        await callbacksRef.current.adapter?.abort(videoId, { upload_id: uploadId, signal });
      },
    });
    setUppy(instance);
    return () => {
      instance.destroy();
      setUppy(null);
    };
  }, [hasAdapter, maxBytes]);

  const runConfirm = useCallback(
    async (opId: number) => {
      const videoId = videoIdRef.current;
      if (!videoId) {
        apply({
          ...IDLE,
          phase: 'error',
          errorKey: 'video.error_completion',
          errorKind: 'completion',
        });
        return;
      }
      apply({
        phase: 'confirming',
        progress: 100,
        fileName: lastFileRef.current?.name ?? null,
        errorKey: null,
        errorKind: null,
      });
      try {
        const result = await callbacksRef.current.adapter?.confirm(videoId);
        if (opRef.current !== opId) return;
        if (!result) throw new Error('confirm failed');
        apply({
          phase: 'completed',
          progress: 100,
          fileName: lastFileRef.current?.name ?? null,
          errorKey: null,
          errorKind: null,
        });
        callbacksRef.current.onCompleted(result.video);
      } catch {
        if (opRef.current !== opId) return;
        apply({
          phase: 'error',
          progress: 100,
          fileName: lastFileRef.current?.name ?? null,
          errorKey: 'video.error_completion',
          errorKind: 'completion',
        });
      }
    },
    [apply],
  );

  // Register byte-transfer listeners once; they read refs, not stale closures.
  useEffect(() => {
    if (!uppy) return;
    const onProgress = (_file: unknown, progress: { bytesUploaded?: number; bytesTotal?: number | null }) => {
      if (phaseRef.current !== 'uploading') return;
      const total = progress.bytesTotal ?? 0;
      const percent = total > 0 ? Math.min(100, Math.round(((progress.bytesUploaded ?? 0) / total) * 100)) : 0;
      setState((prev) => (prev.phase === 'uploading' ? { ...prev, progress: percent } : prev));
    };
    const onSuccess = () => {
      if (phaseRef.current !== 'uploading') return;
      void runConfirm(opRef.current);
    };
    const onError = () => {
      if (phaseRef.current !== 'uploading') return;
      apply({
        phase: 'error',
        progress: 0,
        fileName: lastFileRef.current?.name ?? null,
        errorKey: 'video.error_transfer',
        errorKind: 'transfer',
      });
    };
    uppy.on('upload-progress', onProgress);
    uppy.on('upload-success', onSuccess);
    uppy.on('upload-error', onError);
    return () => {
      uppy.off('upload-progress', onProgress);
      uppy.off('upload-success', onSuccess);
      uppy.off('upload-error', onError);
    };
  }, [uppy, apply, runConfirm]);

  useEffect(() => {
    return () => {
      // Invalidate any in-flight operation so a late resolution can't setState after unmount.
      opRef.current += 1;
    };
  }, []);

  const beginTransfer = useCallback(
    (file: File) => {
      if (!uppy) return;
      opRef.current += 1;
      videoIdRef.current = null;
      lastFileRef.current = file;
      for (const existing of uppy.getFiles()) uppy.removeFile(existing.id);
      apply({ phase: 'uploading', progress: 0, fileName: file.name, errorKey: null, errorKind: null });
      try {
        uppy.addFile({ name: file.name, type: file.type, data: file });
      } catch {
        apply({
          phase: 'error',
          progress: 0,
          fileName: file.name,
          errorKey: 'video.error_transfer',
          errorKind: 'transfer',
        });
        return;
      }
      void uppy.upload();
    },
    [uppy, apply],
  );

  // A file that fails validation never transfers; its error stays in state for the
  // placeholder to show. Only a file that passes the size and type checks is read for
  // its duration, and the placeholder is backed while that runs.
  const startUpload = useCallback(
    async (file: File) => {
      opRef.current += 1;
      const opId = opRef.current;
      videoIdRef.current = null;
      lastFileRef.current = null;
      apply({ ...IDLE, phase: 'checking', fileName: file.name });
      const invalidKey = videoValidationError(file, limits);
      const durationInMs = invalidKey ? null : await readVideoDuration(file);
      if (opRef.current !== opId) return;
      const errorKey = invalidKey ?? videoDurationError(durationInMs, limits);
      if (errorKey) {
        apply({ phase: 'error', progress: 0, fileName: file.name, errorKey, errorKind: 'validation', durationInMs });
        return;
      }
      beginTransfer(file);
    },
    [limits, apply, beginTransfer],
  );

  const retry = useCallback(() => {
    if (state.errorKind === 'completion') {
      opRef.current += 1;
      void runConfirm(opRef.current);
      return;
    }
    if (state.errorKind === 'transfer' && lastFileRef.current) {
      // Request a new upload target and re-send the bytes.
      beginTransfer(lastFileRef.current);
    }
  }, [state.errorKind, runConfirm, beginTransfer]);

  // Clears state back to idle so the panel can accept another file. Used after a
  // completion is acknowledged (the node now owns the id) and by cancel.
  const reset = useCallback(() => {
    opRef.current += 1;
    videoIdRef.current = null;
    lastFileRef.current = null;
    apply(IDLE);
  }, [apply]);

  const cancel = useCallback(() => {
    uppy?.cancelAll();
    reset();
  }, [uppy, reset]);

  const isBusy = state.phase === 'checking' || state.phase === 'uploading' || state.phase === 'confirming';

  // Whether an upload still owns the placeholder: checking the file, running, failed
  // and awaiting retry, or rejected and showing why. Reads a ref, so it is current
  // before React re-renders with the new state.
  const isBacking = useCallback(() => {
    const phase = phaseRef.current;
    return phase === 'checking' || phase === 'uploading' || phase === 'confirming' || phase === 'error';
  }, []);

  return useMemo(
    () => ({ state, startUpload, retry, cancel, reset, isBusy, isBacking }),
    [state, startUpload, retry, cancel, reset, isBusy, isBacking],
  );
};

export type VideoUploadControls = ReturnType<typeof useVideoUpload>;
